import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { Alert, Button, Card, CardContent, PageHeader, formatRelative } from '@marketingbee/ui';
import { Meter, money, QueryView, Section, Stat, useCurrent } from '../components/common';
import { ChannelBadge, formatInZone, pillarName, StatusPill, useActiveBrand } from '../components/marketing';
import { trpc, type RouterOutputs } from '../lib/trpc';

type Summary = RouterOutputs['dashboard']['summary'];
type Item = Summary['needsReview'][number];

export function DashboardPage() {
  const summary = trpc.dashboard.summary.useQuery();
  const current = useCurrent();
  const { brands } = useActiveBrand();
  const onboarding = current.data?.onboarding;
  const brandOf = (id: string) => brands.find((b) => b.id === id);
  const tzOf = (id: string) => brandOf(id)?.profile.timezone ?? current.data?.settings.timezone ?? 'America/Toronto';
  return (
    <div className="space-y-8">
      <PageHeader
        title="Dashboard"
        description={current.data ? `${current.data.name} · ${current.data.entitlements.planName}` : undefined}
        actions={
          <>
            <Button asChild variant="outline">
              <Link to="/calendar">Calendar</Link>
            </Button>
            <Button asChild>
              <Link to="/review">Review</Link>
            </Button>
          </>
        }
      />
      {onboarding && !onboarding.complete && !onboarding.dismissed ? (
        <Alert variant="info" title="Finish setting up">
          Describe your brand, connect a channel and plan your first week. <Link className="font-semibold underline" to="/onboarding">Open the checklist</Link>
        </Alert>
      ) : null}
      <QueryView query={summary} label="Loading dashboard">
        {(s) => (
          <>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Stat label="Waiting for review" value={s.content.needsReview} hint={<Link className="underline" to="/review">Open the queue</Link>} />
              <Stat label="Scheduled" value={s.content.scheduled} hint="Approved and waiting to go out" />
              <Stat label="Published this month" value={s.content.publishedThisMonth} hint={s.content.failed ? `${s.content.failed} failed` : 'None failed'} />
              <Stat label="Clicks (30 days)" value={s.results30d.clicks.toLocaleString('en-CA')} hint={`${s.results30d.signups} sign-ups`} />
            </div>
            <div className="grid gap-6 lg:grid-cols-3">
              <Card>
                <CardContent className="space-y-5 py-6">
                  <h2 className="text-[17px] font-semibold">Plan usage · {s.usage.period}</h2>
                  <Meter label="Social posts" used={s.usage.posts.used} limit={s.usage.posts.limit} unit="" />
                  <p className="text-[13px] text-muted-foreground">Email and blog posts do not count toward the monthly post limit.</p>
                  <p className="text-[13px] text-muted-foreground">
                    Open pipeline: {money(s.pipeline.openValueCents, s.currency)} in {s.pipeline.openDeals} deal{s.pipeline.openDeals === 1 ? '' : 's'}. <Link className="underline" to="/crm/pipeline">Pipeline</Link>
                  </p>
                </CardContent>
              </Card>
              <Section title="Needs review" actions={<Link className="text-sm underline" to="/review">All</Link>}>
                <ItemList items={s.needsReview} empty="Nothing waiting. Plan a week from the Calendar." tzOf={tzOf} pillar={(i) => pillarName(brandOf(i.brandId)?.profile, i.pillarId)} />
              </Section>
              <Section title="Coming up (7 days)" actions={<Link className="text-sm underline" to="/calendar">Calendar</Link>}>
                <ItemList items={s.upcoming} empty="Nothing scheduled this week." tzOf={tzOf} pillar={(i) => pillarName(brandOf(i.brandId)?.profile, i.pillarId)} />
              </Section>
            </div>
            <div className="grid gap-6 lg:grid-cols-2">
              {s.failed.length ? (
                <Section title="Failed to publish">
                  <Card>
                    <CardContent className="divide-y divide-border py-2">
                      {s.failed.map((f) => (
                        <Link key={f.id} to="/content/$id" params={{ id: f.id }} className="block py-3 text-[14px] hover:opacity-80">
                          <span className="flex items-center gap-2">
                            <ChannelBadge channel={f.channel} />
                            <span className="truncate font-medium">{f.title ?? f.body.slice(0, 60)}</span>
                          </span>
                          {f.lastError ? <span className="mt-1 block text-[13px] text-destructive">{f.lastError}</span> : null}
                        </Link>
                      ))}
                    </CardContent>
                  </Card>
                </Section>
              ) : null}
              <Section title="My tasks" actions={<Link className="text-sm underline" to="/crm/tasks">All tasks</Link>}>
                <Card>
                  <CardContent className="divide-y divide-border py-2">
                    {s.dueTasks.length === 0 ? <p className="py-4 text-sm text-muted-foreground">Nothing due this week.</p> : null}
                    {s.dueTasks.map((t) => (
                      <div key={t.id} className="flex items-center justify-between py-3 text-[14px]">
                        <span>{t.title}</span>
                        <span className="text-xs text-muted-foreground">{t.dueAt ? formatRelative(t.dueAt) : ''}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </Section>
            </div>
          </>
        )}
      </QueryView>
    </div>
  );
}

function ItemList({ items, empty, tzOf, pillar }: { items: Item[]; empty: ReactNode; tzOf: (brandId: string) => string; pillar: (i: Item) => string | null }) {
  return (
    <Card>
      <CardContent className="divide-y divide-border py-2">
        {items.length === 0 ? <p className="py-4 text-sm text-muted-foreground">{empty}</p> : null}
        {items.map((i) => (
          <Link key={i.id} to="/content/$id" params={{ id: i.id }} className="block space-y-1 py-3 text-[14px] hover:opacity-80">
            <span className="flex flex-wrap items-center gap-2">
              <ChannelBadge channel={i.channel} />
              <StatusPill status={i.status} />
              <span className="text-[12px] text-muted-foreground">{formatInZone(i.scheduledAt, tzOf(i.brandId))}</span>
            </span>
            <span className="block truncate">{i.humanOnly && !i.body ? 'Write this one yourself' : (i.title ?? (i.body.slice(0, 80) || pillar(i) || 'Planned'))}</span>
          </Link>
        ))}
      </CardContent>
    </Card>
  );
}
